import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">Alaska AI Services</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-invert max-w-none"
        >
          <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
          <p className="text-slate-400 text-lg mb-8">Last updated: February 2026</p>

          <div className="space-y-8 text-slate-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Agreement to Terms</h2>
              <p>
                By accessing or using Alaska AI Services ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of the terms, you may not access the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p>
                Alaska AI Services provides AI-powered virtual receptionist services for businesses. Our Service includes automated call answering, lead capture, appointment scheduling, and a client dashboard for managing leads and viewing analytics.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Account Registration</h2>
              <p className="mb-4">To use our Service, you must:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate and complete business information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Promptly update any changes to your information</li>
                <li>Be at least 18 years old and have authority to bind your business</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Payment Terms</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Setup Fee:</strong> A one-time fee of $1,997 is due at the start of service.</li>
                <li><strong>Monthly Maintenance:</strong> Optional recurring fee of $397/month, billed monthly.</li>
                <li><strong>Payment Processing:</strong> All payments are processed securely through Stripe.</li>
                <li><strong>Refunds:</strong> Subject to our 80% accuracy guarantee (see Section 5).</li>
                <li><strong>Cancellation:</strong> Monthly maintenance can be cancelled anytime with no penalty.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Service Guarantee</h2>
              <p>
                We guarantee that within 30 days of setup completion, your AI receptionist will correctly handle at least 80% of calls, including proper lead qualification, accurate information capture, and appropriate responses. If this target is not met, you are entitled to a full refund of the setup fee. To claim this guarantee, you must notify us in writing within 35 days of setup completion with specific examples of AI failures.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Acceptable Use</h2>
              <p className="mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the Service for any illegal purpose</li>
                <li>Misrepresent your business or services to callers</li>
                <li>Attempt to reverse engineer or copy our AI technology</li>
                <li>Share account access with unauthorized parties</li>
                <li>Use the Service to harass, spam, or deceive callers</li>
                <li>Interfere with or disrupt the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Intellectual Property</h2>
              <p>
                The Service, including all AI technology, software, designs, and content, is owned by Alaska AI Services. You retain ownership of your business information and lead data. You grant us a license to use your business information solely to provide the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Data and Privacy</h2>
              <p>
                Your use of the Service is also governed by our Privacy Policy. You are responsible for ensuring your use of the Service complies with applicable privacy laws, including obtaining any necessary consents for call recording where required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, Alaska AI Services shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities, arising from your use of the Service. Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Disclaimer of Warranties</h2>
              <p>
                The Service is provided "as is" without warranties of any kind, except as expressly stated in our Service Guarantee. We do not guarantee that the Service will be uninterrupted, error-free, or meet all your requirements. AI responses may occasionally be inaccurate or inappropriate.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless Alaska AI Services from any claims, damages, or expenses arising from your use of the Service, your violation of these Terms, or your violation of any third-party rights.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Account Approval and Verification</h2>
              <p className="mb-4">
                Alaska AI Services reserves the right to require verification of identity and business legitimacy before granting access to the Service. We may request documentation to verify:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Your identity and authority to represent the business</li>
                <li>The legitimacy and legal status of your business</li>
                <li>Your business license or registration where applicable</li>
              </ul>
              <p>
                We reserve the sole and absolute discretion to approve or deny any account application, and to refuse service to anyone for any reason without explanation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Termination and Suspension</h2>
              <p className="mb-4">
                We may terminate or suspend your account immediately, without prior notice or liability, for any reason, including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Violation of any provision of these Terms of Service</li>
                <li>Violation of our Privacy Policy</li>
                <li>Fraudulent, deceptive, or illegal activity</li>
                <li>Abuse of our systems, staff, or other users</li>
                <li>Non-payment of fees</li>
                <li>Any conduct we determine, in our sole discretion, to be harmful to our business, reputation, or other users</li>
              </ul>
              <p className="font-semibold text-red-400 bg-red-500/10 p-4 rounded-lg">
                <strong>NO REFUNDS FOR VIOLATIONS:</strong> If your account is terminated due to a violation of these Terms of Service, our Privacy Policy, or any applicable law, you will NOT be entitled to any refund of fees paid, including setup fees, monthly maintenance fees, or any other amounts. This termination policy is final and non-negotiable.
              </p>
              <p className="mt-4">
                Upon termination, your right to use the Service ceases immediately. For accounts terminated in good standing, you may request export of your lead data within 30 days of termination.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">14. Privacy Violations</h2>
              <p className="mb-4">
                The following constitute serious violations of our Privacy Policy and these Terms, and will result in immediate account termination without refund:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Unauthorized access to, or disclosure of, customer data</li>
                <li>Misuse of personally identifiable information</li>
                <li>Recording calls without proper consent where legally required</li>
                <li>Sharing call recordings or transcripts with unauthorized parties</li>
                <li>Using the Service to collect data for purposes not disclosed to callers</li>
                <li>Any violation of TCPA, GDPR, CCPA, or other applicable privacy regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">15. Modifications to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify you of material changes via email or through the Service. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">14. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of Alaska, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of Alaska.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">15. Contact Information</h2>
              <p>
                For questions about these Terms, please contact us at:
              </p>
              <p className="mt-4">
                <strong>Email:</strong> <a href="mailto:info@alaskaaiservices.com" className="text-sky-400 hover:underline">info@alaskaaiservices.com</a><br />
                <strong>Location:</strong> Anchorage, Alaska
              </p>
            </section>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
